/** @jsxImportSource @emotion/react */
import { useMemo, useState } from "react";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { Dropdown } from "primereact/dropdown";
import { SelectButton } from "primereact/selectbutton";
import {
  MEDIDAS,
  agregar,
  desglosesPara,
  dimensionesPara,
  formatearValor
} from "./agregacion";
import TablaAgrupada from "./TablaAgrupada";

// Paleta categórica validada para daltonismo, en orden fijo: la serie N usa
// siempre el color N. "Otros" va en gris para que no compita con las series.
const COLORES = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#6250d6",
  "#e34948"
];
const COLOR_OTROS = "#a3a29c";
const COLOR_TEXTO = "#52514e";
const COLOR_GRILLA = "#e6e5e0";

const TIPOS = [
  { value: "barras", label: "Barras" },
  { value: "apiladas", label: "Apiladas" },
  { value: "lineas", label: "Líneas" },
  { value: "torta", label: "Torta" }
];

// Más de esto en una gráfica de barras ya no se lee; el resto va a "Otros".
const MAX_BARRAS = 15;

const colorDe = (clave, indice) => (clave === "otros" ? COLOR_OTROS : COLORES[indice % COLORES.length]);

// Selector de tipo de gráfica. Los botones toman el fondo oscuro del tema, así
// que el texto va siempre en blanco; la opción elegida se distingue por un
// fondo más oscuro y negrita, y las demás quedan con el tono acento.
const estiloSelectorTipo = {
  "& .p-button": {
    color: "#ffffff",
    background: "var(--principalAcento)",
    borderColor: "var(--principalAcento)",
    boxShadow: "none"
  },
  "& .p-button:not(.p-highlight):hover": {
    color: "#ffffff",
    background: "var(--principal)",
    borderColor: "var(--principal)"
  },
  "& .p-button.p-highlight, & .p-button.p-highlight:hover": {
    color: "#ffffff",
    fontWeight: "bold",
    background: "var(--principal)",
    borderColor: "var(--principal)"
  },
  "& .p-button:focus, & .p-button.p-focus": {
    outline: "none",
    boxShadow: "none"
  }
};

const estiloCampo = {
  display: "flex",
  flexDirection: "column",
  gap: "0.5em",
  minWidth: "12rem",
  flex: "1 1 12rem"
};

/**
 * Gráfica del panel. El usuario elige la medida, por qué se agrupa, un
 * desglose opcional en series y el tipo de gráfica; nada se guarda.
 */
export default function GraficaFacturacion({ facturas, rango, opciones, simbolo }) {
  const [configuracion, setConfiguracion] = useState({
    medida: "facturado",
    dimension: "mes",
    desglose: null,
    tipo: "barras"
  });
  const { medida, dimension, desglose, tipo } = configuracion;

  const dimensiones = dimensionesPara(medida);
  const desgloses = desglosesPara(medida, dimension);

  // Al cambiar algo se descartan las elecciones que dejaron de ser válidas
  // (la antigüedad solo existe para lo pendiente; el desglose no puede ser la
  // misma dimensión; la torta no lleva desglose).
  const cambiar = (clave, valor) =>
    setConfiguracion((c) => {
      const nueva = { ...c, [clave]: valor };
      if (!dimensionesPara(nueva.medida).some((d) => d.value === nueva.dimension)) {
        nueva.dimension = "mes";
      }
      if (
        nueva.tipo === "torta" ||
        !desglosesPara(nueva.medida, nueva.dimension).some((d) => d.value === nueva.desglose)
      ) {
        nueva.desglose = null;
      }
      return nueva;
    });

  const esTemporal = dimensiones.find((d) => d.value === dimension)?.temporal;

  const resultado = useMemo(
    () =>
      agregar(
        facturas,
        rango,
        {
          medida,
          dimension,
          desglose,
          maxCategorias: tipo === "torta" ? COLORES.length : MAX_BARRAS
        },
        opciones
      ),
    [facturas, rango, medida, dimension, desglose, tipo, opciones]
  );

  // La tabla no tiene el límite de barras de la gráfica: lista todas las
  // categorías, así que se agrega aparte.
  const resultadoTabla = useMemo(
    () => agregar(facturas, rango, { medida, dimension, desglose }, opciones),
    [facturas, rango, medida, dimension, desglose, opciones]
  );

  const { data, options, alto } = useMemo(() => {
    const { categorias, series, totales, formato } = resultado;
    const formatear = (v) => formatearValor(v, formato, simbolo);
    const etiquetas = categorias.map((c) => c.etiqueta);

    if (tipo === "torta") {
      return {
        alto: "22rem",
        data: {
          labels: etiquetas,
          datasets: [
            {
              data: totales,
              backgroundColor: categorias.map((c, i) => colorDe(c.clave, i)),
              borderColor: "#ffffff",
              borderWidth: 2
            }
          ]
        },
        options: {
          maintainAspectRatio: false,
          plugins: {
            legend: { position: "right", labels: { color: COLOR_TEXTO } },
            tooltip: {
              callbacks: { label: (ctx) => `${ctx.label}: ${formatear(ctx.raw)}` }
            }
          }
        }
      };
    }

    // Las categorías con nombre (clientes, tipos) van en barras horizontales
    // para que los nombres largos se lean; el tiempo va en el eje horizontal.
    const horizontal = tipo !== "lineas" && !esTemporal;
    const apilada = tipo === "apiladas";
    const ejeValor = {
      stacked: apilada,
      beginAtZero: true,
      grid: { color: COLOR_GRILLA },
      ticks: {
        color: COLOR_TEXTO,
        precision: formato === "cantidad" ? 0 : undefined,
        callback: (v) => formatear(v)
      }
    };
    const ejeCategoria = {
      stacked: apilada,
      grid: { display: false },
      ticks: { color: COLOR_TEXTO }
    };

    return {
      alto: horizontal
        ? `${Math.max(22, categorias.length * (apilada || series.length === 1 ? 2 : series.length * 1.2) + 4)}rem`
        : "22rem",
      data: {
        labels: etiquetas,
        datasets: series.map((s, i) => {
          const color = colorDe(s.clave, i);
          return tipo === "lineas"
            ? {
                label: s.etiqueta,
                data: s.valores,
                borderColor: color,
                backgroundColor: color,
                borderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 6,
                tension: 0
              }
            : {
                label: s.etiqueta,
                data: s.valores,
                backgroundColor: color,
                borderRadius: 4,
                borderSkipped: "start",
                maxBarThickness: 40
              };
        })
      },
      options: {
        maintainAspectRatio: false,
        indexAxis: horizontal ? "y" : "x",
        interaction: { mode: "index", intersect: false },
        scales: horizontal
          ? { x: ejeValor, y: ejeCategoria }
          : { x: ejeCategoria, y: ejeValor },
        plugins: {
          legend: {
            display: series.length > 1,
            labels: { color: COLOR_TEXTO }
          },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                `${ctx.dataset.label}: ${formatear(horizontal ? ctx.parsed.x : ctx.parsed.y)}`
            }
          }
        }
      }
    };
  }, [resultado, tipo, esTemporal, simbolo]);

  const sinDatos = resultado.totales.every((v) => !v);

  return (
    <Card css={{ marginBottom: "1em" }}>
      <div
        css={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1em",
          alignItems: "flex-end",
          marginBottom: "1.5em"
        }}
      >
        <div css={estiloCampo}>
          <label css={{ fontWeight: "bold" }}>Medida</label>
          <Dropdown
            value={medida}
            options={MEDIDAS}
            onChange={(e) => cambiar("medida", e.value)}
          />
        </div>
        <div css={estiloCampo}>
          <label css={{ fontWeight: "bold" }}>Agrupar por</label>
          <Dropdown
            value={dimension}
            options={dimensiones}
            onChange={(e) => cambiar("dimension", e.value)}
          />
        </div>
        <div css={estiloCampo}>
          <label css={{ fontWeight: "bold" }}>Desglosar por</label>
          <Dropdown
            value={desglose}
            options={desgloses}
            onChange={(e) => cambiar("desglose", e.value ?? null)}
            placeholder="Sin desglose"
            showClear
            disabled={tipo === "torta"}
          />
        </div>
        <SelectButton
          value={tipo}
          options={TIPOS}
          onChange={(e) => e.value && cambiar("tipo", e.value)}
          allowEmpty={false}
          css={estiloSelectorTipo}
        />
      </div>

      {sinDatos ? (
        <div css={{ textAlign: "center", padding: "2em" }}>
          No hay datos para mostrar.
        </div>
      ) : (
        <Chart
          key={tipo}
          type={tipo === "torta" ? "pie" : tipo === "lineas" ? "line" : "bar"}
          data={data}
          options={options}
          style={{ height: alto, position: "relative" }}
        />
      )}

      {!sinDatos && (
        <div css={{ marginTop: "1.5em" }}>
          <TablaAgrupada
            resultado={resultadoTabla}
            etiquetaDimension={dimensiones.find((d) => d.value === dimension)?.label}
            conDesglose={desglose != null}
            simbolo={simbolo}
            nombreArchivo={`indicadores_${medida}_por_${dimension}`}
          />
        </div>
      )}
    </Card>
  );
}
